/**
 * DIPS2.0 登録機体一覧ページ
 */

import React, { useState, useEffect, useCallback } from 'react';
import { dipsApi } from '../services/api';
import { Icon } from '../components/icons';
import { Button, Card, Badge, Alert, Modal } from '../components/ui';

// 機体種類のラベル
const AIRCRAFT_TYPE_LABELS = {
  '1': '回転翼（マルチローター）',
  '2': '回転翼（ヘリコプター）',
  '3': '固定翼',
  '4': '固定翼（VTOL）',
};

// 所有者区分のラベル
const OWNER_CLASSIFICATION_LABELS = {
  '1': '個人',
  '9': '法人',
};

// ヘルパー関数: ネストされたデータを取得
const getAircraftInfo = (aircraft) => aircraft?.aircraft_information || aircraft;
const getOwnerInfo = (aircraft) => aircraft?.owner_information;
const getUserInfo = (aircraft) => aircraft?.user_information;

// 日付フォーマット
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('ja-JP');
  } catch {
    return dateStr;
  }
};

export default function DipsAircrafts() {
  const [dipsAircrafts, setDipsAircrafts] = useState(null);
  const [dipsLoading, setDipsLoading] = useState(false);
  const [dipsError, setDipsError] = useState(null);
  const [dipsAuthenticated, setDipsAuthenticated] = useState(dipsApi.isAuthenticated());
  const [selectedAircraft, setSelectedAircraft] = useState(null);

  // 飛行計画
  const [flightPlans, setFlightPlans] = useState([]);
  const [flightPlansLoading, setFlightPlansLoading] = useState(false);
  const [flightPlansError, setFlightPlansError] = useState(null);
  const [selectedFlightPlan, setSelectedFlightPlan] = useState(null);
  const [flightPlanAircraft, setFlightPlanAircraft] = useState(null); // 飛行計画を表示中の機体
  const [flightPlanAuthenticated, setFlightPlanAuthenticated] = useState(dipsApi.isFlightPlanAuthenticated());
  const [flightPlanAuthLoading, setFlightPlanAuthLoading] = useState(false);
  const [searchDate, setSearchDate] = useState(() => {
    // デフォルトは今日
    const today = new Date();
    return today.toISOString().split('T')[0]; // "YYYY-MM-DD"
  });
  const [scanMode, setScanMode] = useState(false); // 範囲スキャンモード
  const [scanProgress, setScanProgress] = useState('');
  const [scanResults, setScanResults] = useState([]); // スキャン結果

  // 飛行計画登録フォーム
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportResult, setReportResult] = useState(null);
  const [reportForm, setReportForm] = useState({
    // 飛行日時
    flightDate: new Date().toISOString().split('T')[0],
    startTime: '09:00',
    endTime: '17:00',
    // 飛行エリア（緯度・経度）
    latitude: '35.6895',  // 東京
    longitude: '139.6917',
    radius: '500', // メートル
    // 飛行目的
    purpose: '空撮',
    // 高度
    maxAltitude: '150',
    // 備考
    remarks: '',
  });

  // DIPS機体情報取得
  const fetchDipsAircrafts = useCallback(async () => {
    if (!dipsApi.isAuthenticated()) {
      setDipsAuthenticated(false);
      return;
    }
    setDipsLoading(true);
    setDipsError(null);
    try {
      const data = await dipsApi.getAircrafts();
      setDipsAircrafts(data);
      setDipsAuthenticated(true);
    } catch (err) {
      setDipsError(err.message);
      if (err.message.includes('認証')) {
        setDipsAuthenticated(false);
      }
    } finally {
      setDipsLoading(false);
    }
  }, []);

  // 日付を "yyyyMMdd HHmm" 形式にフォーマット
  const formatDipsDateTime = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${y}${m}${d} ${h}${min}`;
  };

  // DIPS飛行計画取得（地理的範囲と日時で検索）
  const fetchFlightPlans = useCallback(async () => {
    console.log('[DIPS] fetchFlightPlans called, searchDate:', searchDate);
    console.log('[DIPS] isFlightPlanAuthenticated:', dipsApi.isFlightPlanAuthenticated());

    if (!dipsApi.isFlightPlanAuthenticated()) {
      console.log('[DIPS] Flight plan not authenticated, skipping');
      return;
    }

    setFlightPlansLoading(true);
    setFlightPlansError(null);
    try {
      // 選択した日付の00:00から23:59まで（24時間以内）
      const [year, month, day] = searchDate.split('-').map(Number);
      const startTime = new Date(year, month - 1, day, 0, 0);
      const finishTime = new Date(year, month - 1, day, 23, 59);

      // FPR APIの正しいリクエスト構造
      // features: 検索範囲（日本全体をカバーする広い円）
      // 東京を中心に半径500km
      const searchParams = {
        features: {
          type: 'Circle',
          center: [139.6917, 35.6895], // 東京
          radius: 500000, // 500km
        },
        startTime: formatDipsDateTime(startTime),
        finishTime: formatDipsDateTime(finishTime),
        allFlightPlan: '1', // 自分の飛行計画のみ
      };

      console.log('[DIPS] Flight plan search params:', searchParams);
      const data = await dipsApi.getFlightPlans(searchParams);
      console.log('[DIPS] Flight plans response:', data);

      // レスポンスから飛行計画リストを抽出
      const plans = Array.isArray(data) ? data : data.flight_plans || data.flightPlans || data.items || data.results || [];
      setFlightPlans(plans);
    } catch (err) {
      console.error('[DIPS] Flight plans error:', err);
      setFlightPlansError(err.message);
    } finally {
      setFlightPlansLoading(false);
    }
  }, [searchDate]);

  // DIPS認証開始
  const handleDipsAuth = async () => {
    console.log('[DIPS] handleDipsAuth called');
    setDipsLoading(true);
    setDipsError(null);
    try {
      console.log('[DIPS] Getting auth URL...');
      const result = await dipsApi.getAuthUrl('aircraft');
      console.log('[DIPS] Auth URL result:', result);
      const { authUrl } = result;

      if (!authUrl) {
        throw new Error('認証URLが取得できませんでした');
      }

      // postMessageイベントリスナーを設定
      const handleMessage = (event) => {
        console.log('[DIPS] Received postMessage:', event.data);
        if (event.data?.type === 'DIPS_AUTH_SUCCESS') {
          console.log('[DIPS] Received auth success via postMessage');
          dipsApi.storeToken({
            accessToken: event.data.accessToken,
            refreshToken: event.data.refreshToken,
            expiresIn: event.data.expiresIn,
          });
          setDipsAuthenticated(true);
          setDipsLoading(false);
          fetchDipsAircrafts();
          window.removeEventListener('message', handleMessage);
        } else if (event.data?.type === 'DIPS_AUTH_ERROR') {
          console.error('[DIPS] Received auth error via postMessage:', event.data.error);
          setDipsError('認証エラー: ' + event.data.error);
          setDipsLoading(false);
          window.removeEventListener('message', handleMessage);
        }
      };
      window.addEventListener('message', handleMessage);
      console.log('[DIPS] postMessage listener added');

      console.log('[DIPS] Opening auth window:', authUrl);
      const authWindow = window.open(authUrl, 'dips_auth', 'width=600,height=700');

      if (!authWindow) {
        window.removeEventListener('message', handleMessage);
        throw new Error('ポップアップがブロックされました。ポップアップを許可してください。');
      }

      const checkAuth = setInterval(() => {
        try {
          if (authWindow?.closed) {
            clearInterval(checkAuth);
            console.log('[DIPS] Auth window closed, checking token...');
            setTimeout(() => {
              window.removeEventListener('message', handleMessage);
              if (dipsApi.isAuthenticated()) {
                console.log('[DIPS] Token found after window close');
                setDipsAuthenticated(true);
                setDipsLoading(false);
                fetchDipsAircrafts();
              } else {
                console.log('[DIPS] No token found after window close');
                setDipsLoading(false);
              }
            }, 1000);
          }
        } catch {
          // Cross-origin エラーは無視
        }
      }, 500);
    } catch (err) {
      console.error('[DIPS] Auth error:', err);
      setDipsError('認証エラー: ' + err.message);
      setDipsLoading(false);
    }
  };

  // DIPS認証済みなら機体情報を自動取得
  useEffect(() => {
    if (dipsAuthenticated && !dipsAircrafts) {
      fetchDipsAircrafts();
    }
  }, [dipsAuthenticated, dipsAircrafts, fetchDipsAircrafts]);

  // 認証完了時または日付変更時に飛行計画を取得
  useEffect(() => {
    console.log('[DIPS] useEffect check - flightPlanAuthenticated:', flightPlanAuthenticated, 'searchDate:', searchDate);
    if (flightPlanAuthenticated && !scanMode) {
      console.log('[DIPS] Calling fetchFlightPlans from useEffect');
      fetchFlightPlans();
    }
  }, [flightPlanAuthenticated, searchDate, scanMode, fetchFlightPlans]);

  // 範囲スキャン: 過去2週間 + 未来2ヶ月を検索
  const scanDateRange = async () => {
    if (!dipsApi.isFlightPlanAuthenticated()) {
      setFlightPlansError('飛行計画認証が必要です');
      return;
    }

    setScanMode(true);
    setScanResults([]);
    setFlightPlans([]);
    setFlightPlansError(null);

    const today = new Date();
    const results = [];

    // 過去2週間（14日）+ 未来2ヶ月（60日）= 約74日
    const startOffset = -14; // 2週間前
    const endOffset = 60;    // 2ヶ月後

    for (let i = startOffset; i <= endOffset; i++) {
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + i);

      const dateStr = targetDate.toISOString().split('T')[0];
      setScanProgress(`検索中: ${dateStr} (${i - startOffset + 1}/${endOffset - startOffset + 1})`);

      try {
        const startTime = new Date(targetDate);
        startTime.setHours(0, 0, 0, 0);
        const finishTime = new Date(targetDate);
        finishTime.setHours(23, 59, 0, 0);

        const searchParams = {
          features: {
            type: 'Circle',
            center: [139.6917, 35.6895],
            radius: 500000,
          },
          startTime: formatDipsDateTime(startTime),
          finishTime: formatDipsDateTime(finishTime),
          allFlightPlan: '1',
        };

        const data = await dipsApi.getFlightPlans(searchParams);
        const plans = Array.isArray(data) ? data : data.flight_plans || data.flightPlans || data.items || data.results || [];

        if (plans.length > 0) {
          results.push({
            date: dateStr,
            count: plans.length,
            plans: plans,
          });
          console.log(`[DIPS] Found ${plans.length} plans on ${dateStr}`);
        }
      } catch (err) {
        // エラーは無視して続行
        console.log(`[DIPS] Error on ${dateStr}:`, err.message);
      }

      // API負荷軽減のため少し待機
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setScanProgress('');
    setScanResults(results);
    setScanMode(false);

    console.log('[DIPS] Scan complete. Results:', results);
  };

  // 飛行計画表示用（機体選択は不要になったが互換性のため残す）
  const handleShowFlightPlans = (aircraft) => {
    console.log('[DIPS] handleShowFlightPlans called');
    setFlightPlanAircraft(aircraft);
  };

  // 飛行計画認証開始
  const handleFlightPlanAuth = async () => {
    console.log('[DIPS] handleFlightPlanAuth called');
    setFlightPlanAuthLoading(true);
    setFlightPlansError(null);
    try {
      const result = await dipsApi.getAuthUrl('flightPlan');
      const { authUrl } = result;

      if (!authUrl) {
        throw new Error('認証URLが取得できませんでした');
      }

      // postMessageイベントリスナーを設定
      const handleMessage = (event) => {
        console.log('[DIPS FlightPlan] Received postMessage:', event.data);
        if (event.data?.type === 'DIPS_AUTH_SUCCESS') {
          console.log('[DIPS FlightPlan] Received auth success via postMessage');
          dipsApi.storeFlightPlanToken({
            accessToken: event.data.accessToken,
            refreshToken: event.data.refreshToken,
            expiresIn: event.data.expiresIn,
          });
          setFlightPlanAuthenticated(true);
          setFlightPlanAuthLoading(false);
          window.removeEventListener('message', handleMessage);
        } else if (event.data?.type === 'DIPS_AUTH_ERROR') {
          console.error('[DIPS FlightPlan] Received auth error:', event.data.error);
          setFlightPlansError('認証エラー: ' + event.data.error);
          setFlightPlanAuthLoading(false);
          window.removeEventListener('message', handleMessage);
        }
      };
      window.addEventListener('message', handleMessage);

      const authWindow = window.open(authUrl, 'dips_flightplan_auth', 'width=600,height=700');

      if (!authWindow) {
        window.removeEventListener('message', handleMessage);
        throw new Error('ポップアップがブロックされました。');
      }

      const checkAuth = setInterval(() => {
        try {
          if (authWindow?.closed) {
            clearInterval(checkAuth);
            setTimeout(() => {
              window.removeEventListener('message', handleMessage);
              if (dipsApi.isFlightPlanAuthenticated()) {
                setFlightPlanAuthenticated(true);
                setFlightPlanAuthLoading(false);
              } else {
                setFlightPlanAuthLoading(false);
              }
            }, 1000);
          }
        } catch {
          // Cross-origin エラーは無視
        }
      }, 500);
    } catch (err) {
      console.error('[DIPS FlightPlan] Auth error:', err);
      setFlightPlansError('認証エラー: ' + err.message);
      setFlightPlanAuthLoading(false);
    }
  };

  // 飛行計画通報（登録）
  const handleReportFlightPlan = async () => {
    if (!dipsApi.isFlightPlanAuthenticated()) {
      setFlightPlansError('飛行計画認証が必要です');
      return;
    }

    setReportLoading(true);
    setReportResult(null);
    setFlightPlansError(null);

    try {
      // 日時をDIPS形式に変換
      const [year, month, day] = reportForm.flightDate.split('-').map(Number);
      const [startH, startM] = reportForm.startTime.split(':').map(Number);
      const [endH, endM] = reportForm.endTime.split(':').map(Number);

      const startDateTime = new Date(year, month - 1, day, startH, startM);
      const endDateTime = new Date(year, month - 1, day, endH, endM);

      // FPR API リクエストボディ
      const reportData = {
        // 飛行エリア（円形）
        features: {
          type: 'Circle',
          center: [parseFloat(reportForm.longitude), parseFloat(reportForm.latitude)],
          radius: parseInt(reportForm.radius, 10),
        },
        // 飛行開始・終了時刻
        startTime: formatDipsDateTime(startDateTime),
        finishTime: formatDipsDateTime(endDateTime),
        // 飛行目的
        flightPurpose: reportForm.purpose,
        // 最大高度
        maxAltitude: parseInt(reportForm.maxAltitude, 10),
        // 備考
        remarks: reportForm.remarks || undefined,
      };

      console.log('[DIPS] Report flight plan:', reportData);
      const result = await dipsApi.reportFlightPlan(reportData);
      console.log('[DIPS] Report result:', result);

      setReportResult({ success: true, data: result });
      // 成功したら検索を再実行
      fetchFlightPlans();
    } catch (err) {
      console.error('[DIPS] Report error:', err);
      setReportResult({ success: false, error: err.message });
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">DIPS2.0 連携</h1>
          <p className="text-sm text-slate-500 mt-1">
            国土交通省ドローン情報基盤システムとの連携
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dipsAuthenticated ? (
            <>
              <Badge variant="success" size="md">連携済み</Badge>
              <Button variant="secondary" onClick={fetchDipsAircrafts} disabled={dipsLoading}>
                <Icon name="refresh" className="w-4 h-4" />
                同期
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  dipsApi.clearToken();
                  setDipsAuthenticated(false);
                  setDipsAircrafts(null);
                  const logoutWindow = window.open(
                    'https://www.dips-regdev.mlit.go.jp/auth/realms/drs-utm/protocol/openid-connect/logout',
                    'dips_logout',
                    'width=500,height=400'
                  );
                  if (logoutWindow) {
                    setTimeout(() => {
                      logoutWindow.close();
                    }, 500);
                  }
                }}
              >
                <Icon name="logout" className="w-4 h-4" />
                ログアウト
              </Button>
            </>
          ) : (
            <Button onClick={handleDipsAuth} disabled={dipsLoading}>
              <Icon name="link" className="w-4 h-4" />
              {dipsLoading ? '接続中...' : 'DIPS2.0認証'}
            </Button>
          )}
        </div>
      </div>

      {/* エラー表示 */}
      {dipsError && (
        <Alert variant="error">{dipsError}</Alert>
      )}

      {/* 未認証状態（ローディング中は非表示） */}
      {!dipsAuthenticated && !dipsError && !dipsLoading && (
        <Card>
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-6 bg-slate-100 rounded-full flex items-center justify-center">
              <Icon name="link" className="w-10 h-10 text-slate-400" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">
              DIPS2.0と連携する
            </h2>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              国土交通省のドローン情報基盤システム（DIPS2.0）と連携すると、
              登録されている機体情報を表示できます。
            </p>
            <Button size="lg" onClick={handleDipsAuth} disabled={dipsLoading}>
              <Icon name="link" className="w-5 h-5" />
              DIPS2.0認証を開始
            </Button>
          </div>
        </Card>
      )}

      {/* ローディング */}
      {dipsLoading && (
        <Card>
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-700 font-medium">機体情報を取得中...</p>
              <p className="text-slate-500 text-sm mt-1">DIPS2.0と通信しています</p>
            </div>
          </div>
        </Card>
      )}

      {/* 所有者・使用者情報（上部に1回だけ表示） */}
      {dipsAuthenticated && !dipsLoading && dipsAircrafts?.length > 0 && (() => {
        const firstAircraft = dipsAircrafts[0];
        const owner = getOwnerInfo(firstAircraft);
        const user = getUserInfo(firstAircraft);
        const isUserDifferent = user && user.owner_user_same_confirmation === '2';

        return (
          <div className={`grid gap-4 ${isUserDifferent ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
            {/* 所有者情報カード */}
            {owner && (
              <Card title={
                <span className="flex items-center gap-2">
                  <Icon name="user" className="w-4 h-4" />
                  所有者情報
                  <Badge variant="info" size="sm">
                    {OWNER_CLASSIFICATION_LABELS[owner.owner_classification] || owner.owner_classification}
                  </Badge>
                </span>
              }>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <DetailRow label="氏名/法人名" value={owner.owner_fullname || owner.owner_corporation_name} />
                  <DetailRow label="フリガナ" value={owner.owner_furigana} />
                  <DetailRow label="住所" value={owner.owner_address} />
                  <DetailRow label="電話番号" value={owner.owner_tel} />
                  <DetailRow label="メールアドレス" value={owner.owner_email_address} />
                  {owner.owner_birthday && <DetailRow label="生年月日" value={owner.owner_birthday} />}
                </div>
              </Card>
            )}

            {/* 使用者情報カード（所有者と異なる場合のみ） */}
            {isUserDifferent && (
              <Card title={
                <span className="flex items-center gap-2">
                  <Icon name="users" className="w-4 h-4" />
                  使用者情報
                  <Badge variant="info" size="sm">
                    {OWNER_CLASSIFICATION_LABELS[user.user_classification] || user.user_classification}
                  </Badge>
                </span>
              }>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <DetailRow label="氏名/法人名" value={user.user_fullname || user.user_corporation_name} />
                  <DetailRow label="フリガナ" value={user.user_furigana} />
                  <DetailRow label="住所" value={user.user_address} />
                  <DetailRow label="電話番号" value={user.user_tel} />
                  <DetailRow label="メールアドレス" value={user.user_email_address} />
                  {user.user_corporation_representative_name && (
                    <DetailRow label="代表者名" value={user.user_corporation_representative_name} />
                  )}
                  {user.user_department_name && (
                    <DetailRow label="部署名" value={user.user_department_name} />
                  )}
                </div>
              </Card>
            )}
          </div>
        );
      })()}

      {/* 機体一覧 */}
      {dipsAuthenticated && !dipsLoading && (() => {
        // 有効な機体を先に、無効な機体を後に表示
        const sortedAircrafts = dipsAircrafts ? [...dipsAircrafts].sort((a, b) => {
          const statusA = getAircraftInfo(a)?.aircraft_status;
          const statusB = getAircraftInfo(b)?.aircraft_status;
          // '1'（有効）を先に
          if (statusA === '1' && statusB !== '1') return -1;
          if (statusA !== '1' && statusB === '1') return 1;
          return 0;
        }) : [];

        const activeCount = sortedAircrafts.filter(a => getAircraftInfo(a)?.aircraft_status === '1').length;
        const inactiveCount = sortedAircrafts.length - activeCount;

        return (
          <Card
            title="登録機体一覧"
            headerActions={
              <div className="flex items-center gap-3 text-sm">
                <span className="text-slate-500">
                  {sortedAircrafts.length}機
                </span>
                {activeCount > 0 && (
                  <Badge variant="success" size="sm">有効 {activeCount}</Badge>
                )}
                {inactiveCount > 0 && (
                  <Badge variant="default" size="sm">無効 {inactiveCount}</Badge>
                )}
              </div>
            }
          >
            {sortedAircrafts.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {sortedAircrafts.map((aircraft, idx) => {
                  const info = getAircraftInfo(aircraft);
                  const isActive = info.aircraft_status === '1';
                  return (
                    <div
                      key={idx}
                      className={`p-4 rounded-lg border transition-all cursor-pointer ${
                        isActive
                          ? 'bg-slate-50 border-slate-200 hover:border-primary-300 hover:shadow-sm'
                          : 'bg-slate-100 border-slate-300 opacity-70 hover:opacity-100'
                      }`}
                      onClick={() => setSelectedAircraft(aircraft)}
                    >
                      {/* ヘッダー：登録記号とステータス */}
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className={`font-semibold ${isActive ? 'text-slate-900' : 'text-slate-600'}`}>
                            {info.registration_code || '—'}
                          </div>
                          <div className="text-sm text-slate-500 mt-0.5">
                            {info.manufacturer_jpn || info.manufacturer_eng} / {info.model_jpn || info.model_eng}
                          </div>
                        </div>
                        <Badge
                          variant={isActive ? 'success' : 'default'}
                          size="sm"
                        >
                          {isActive ? '有効' :
                           info.aircraft_status === '2' ? '期限切れ' : '抹消済'}
                        </Badge>
                      </div>

                      {/* 概要情報 */}
                      <div className="space-y-2 text-sm">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <span className="text-slate-400">製造番号:</span>{' '}
                            <span className="text-slate-700">{info.manufacturing_number || '—'}</span>
                          </div>
                          <div>
                            <span className="text-slate-400">RID製造番号:</span>{' '}
                            <span className="text-slate-700">{info.rid_manufacturing_number || '—'}</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <span className="text-slate-400">登録日:</span>{' '}
                            <span className="text-slate-700">{formatDate(info.effectiveness_period_self)}</span>
                          </div>
                          <div>
                            <span className="text-slate-400">有効期限:</span>{' '}
                            <span className="text-slate-700">{formatDate(info.effectiveness_period_to)}</span>
                          </div>
                        </div>
                        <div>
                          <span className="text-slate-400">機体種類:</span>{' '}
                          <span className="text-slate-700">
                            {AIRCRAFT_TYPE_LABELS[info.aircraft_type] || info.aircraft_type || '—'}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400">重量:</span>{' '}
                          <span className="text-slate-700">{info.aircraft_weight}kg</span>
                          <span className="text-slate-400 ml-3">最大離陸重量:</span>{' '}
                          <span className="text-slate-700">{info.maximum_takeoff_weight}kg</span>
                        </div>
                      </div>

                      {/* アクションボタン */}
                      <div className="mt-3 pt-3 border-t border-slate-200 flex justify-between items-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowFlightPlans(aircraft);
                          }}
                          className="text-xs text-amber-600 hover:text-amber-800 flex items-center gap-1 px-2 py-1 rounded hover:bg-amber-50"
                        >
                          <Icon name="calendar" className="w-3 h-3" />
                          飛行計画
                        </button>
                        <span className="text-xs text-primary-500 flex items-center gap-1">
                          詳細を見る
                          <Icon name="chevronRight" className="w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Icon name="drone" className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>DIPS2.0に登録されている機体はありません</p>
              </div>
            )}
          </Card>
        );
      })()}

      {/* 飛行計画セクション */}
      {dipsAuthenticated && !dipsLoading && (
        <Card
          title={
            <span className="flex items-center gap-2">
              <Icon name="calendar" className="w-4 h-4" />
              飛行計画
              {flightPlanAuthenticated ? (
                <Badge variant="success" size="sm">認証済み</Badge>
              ) : (
                <Badge variant="default" size="sm">要認証</Badge>
              )}
            </span>
          }
          headerActions={
            <div className="flex items-center gap-2">
              {flightPlanAuthenticated && (
                <>
                  <input
                    type="date"
                    value={searchDate}
                    onChange={(e) => setSearchDate(e.target.value)}
                    className="px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={scanMode}
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => { setScanResults([]); fetchFlightPlans(); }}
                    disabled={flightPlansLoading || scanMode}
                  >
                    <Icon name="refresh" className="w-3 h-3" />
                    検索
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={scanDateRange}
                    disabled={flightPlansLoading || scanMode}
                  >
                    <Icon name="search" className="w-3 h-3" />
                    {scanMode ? scanProgress : '範囲スキャン'}
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => setShowReportForm(!showReportForm)}
                  >
                    <Icon name="plus" className="w-3 h-3" />
                    飛行計画通報
                  </Button>
                </>
              )}
              {!flightPlanAuthenticated && (
                <Button
                  size="sm"
                  onClick={handleFlightPlanAuth}
                  disabled={flightPlanAuthLoading}
                >
                  <Icon name="link" className="w-3 h-3" />
                  {flightPlanAuthLoading ? '認証中...' : '飛行計画認証'}
                </Button>
              )}
            </div>
          }
        >
          {/* 飛行計画通報フォーム */}
          {showReportForm && flightPlanAuthenticated && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="text-sm font-medium text-slate-700 mb-4 flex items-center gap-2">
                <Icon name="plus" className="w-4 h-4" />
                飛行計画通報（新規登録）
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* 飛行日 */}
                <div>
                  <label className="block text-xs text-slate-500 mb-1">飛行日</label>
                  <input
                    type="date"
                    value={reportForm.flightDate}
                    onChange={(e) => setReportForm({ ...reportForm, flightDate: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* 開始時刻 */}
                <div>
                  <label className="block text-xs text-slate-500 mb-1">開始時刻</label>
                  <input
                    type="time"
                    value={reportForm.startTime}
                    onChange={(e) => setReportForm({ ...reportForm, startTime: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* 終了時刻 */}
                <div>
                  <label className="block text-xs text-slate-500 mb-1">終了時刻</label>
                  <input
                    type="time"
                    value={reportForm.endTime}
                    onChange={(e) => setReportForm({ ...reportForm, endTime: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* 緯度 */}
                <div>
                  <label className="block text-xs text-slate-500 mb-1">緯度</label>
                  <input
                    type="text"
                    value={reportForm.latitude}
                    onChange={(e) => setReportForm({ ...reportForm, latitude: e.target.value })}
                    placeholder="例: 35.6895"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* 経度 */}
                <div>
                  <label className="block text-xs text-slate-500 mb-1">経度</label>
                  <input
                    type="text"
                    value={reportForm.longitude}
                    onChange={(e) => setReportForm({ ...reportForm, longitude: e.target.value })}
                    placeholder="例: 139.6917"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* 半径 */}
                <div>
                  <label className="block text-xs text-slate-500 mb-1">半径 (m)</label>
                  <input
                    type="number"
                    value={reportForm.radius}
                    onChange={(e) => setReportForm({ ...reportForm, radius: e.target.value })}
                    placeholder="例: 500"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* 飛行目的 */}
                <div>
                  <label className="block text-xs text-slate-500 mb-1">飛行目的</label>
                  <select
                    value={reportForm.purpose}
                    onChange={(e) => setReportForm({ ...reportForm, purpose: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="空撮">空撮</option>
                    <option value="測量">測量</option>
                    <option value="点検">点検</option>
                    <option value="農薬散布">農薬散布</option>
                    <option value="物資輸送">物資輸送</option>
                    <option value="趣味">趣味</option>
                    <option value="その他">その他</option>
                  </select>
                </div>

                {/* 最大高度 */}
                <div>
                  <label className="block text-xs text-slate-500 mb-1">最大高度 (m)</label>
                  <input
                    type="number"
                    value={reportForm.maxAltitude}
                    onChange={(e) => setReportForm({ ...reportForm, maxAltitude: e.target.value })}
                    placeholder="例: 150"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* 備考 */}
                <div className="md:col-span-2 lg:col-span-1">
                  <label className="block text-xs text-slate-500 mb-1">備考</label>
                  <input
                    type="text"
                    value={reportForm.remarks}
                    onChange={(e) => setReportForm({ ...reportForm, remarks: e.target.value })}
                    placeholder="任意"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              {/* 送信ボタン */}
              <div className="mt-4 flex items-center gap-3">
                <Button
                  variant="success"
                  onClick={handleReportFlightPlan}
                  disabled={reportLoading}
                >
                  <Icon name="send" className="w-4 h-4" />
                  {reportLoading ? '送信中...' : '飛行計画を通報'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setShowReportForm(false)}
                >
                  キャンセル
                </Button>
              </div>

              {/* 結果表示 */}
              {reportResult && (
                <div className="mt-4">
                  {reportResult.success ? (
                    <Alert variant="success">
                      飛行計画を通報しました
                      <pre className="mt-2 text-xs bg-white p-2 rounded overflow-auto max-h-32">
                        {JSON.stringify(reportResult.data, null, 2)}
                      </pre>
                    </Alert>
                  ) : (
                    <Alert variant="error">
                      エラー: {reportResult.error}
                    </Alert>
                  )}
                </div>
              )}
            </div>
          )}

          {!flightPlanAuthenticated ? (
            <div className="text-center py-8 text-slate-500">
              <Icon name="lock" className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p>飛行計画の表示には別途認証が必要です</p>
              <p className="text-sm mt-1">右上の「飛行計画認証」ボタンを押してください</p>
            </div>
          ) : scanMode ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-8 h-8 border-3 border-primary-500 border-t-transparent rounded-full animate-spin mb-3" />
              <span className="text-slate-700 font-medium">{scanProgress}</span>
              <span className="text-sm text-slate-500 mt-1">過去2週間〜未来2ヶ月をスキャン中...</span>
            </div>
          ) : flightPlansLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-3 border-primary-500 border-t-transparent rounded-full animate-spin" />
              <span className="ml-3 text-slate-500">飛行計画を取得中...</span>
            </div>
          ) : flightPlansError ? (
            <div className="py-8 text-center">
              <Alert variant="warning">
                飛行計画の取得に失敗しました: {flightPlansError}
              </Alert>
              <p className="text-sm text-slate-500 mt-2">
                飛行計画APIには別途認証が必要な場合があります
              </p>
            </div>
          ) : scanResults.length > 0 ? (
            // スキャン結果表示
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-slate-600 pb-2 border-b">
                <Icon name="check" className="w-4 h-4 text-green-500" />
                スキャン完了: {scanResults.reduce((sum, r) => sum + r.count, 0)}件の飛行計画を{scanResults.length}日で発見
              </div>
              <div className="space-y-2">
                {scanResults.map((result, idx) => (
                  <div
                    key={idx}
                    className="p-3 bg-amber-50 border border-amber-200 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium text-slate-900">
                        {result.date}
                      </div>
                      <Badge variant="warning" size="sm">
                        {result.count}件
                      </Badge>
                    </div>
                    {/* 各日の飛行計画リスト */}
                    <div className="space-y-1">
                      {result.plans.map((plan, pIdx) => (
                        <div
                          key={pIdx}
                          className="text-sm p-2 bg-white rounded border border-amber-100 hover:bg-amber-50 cursor-pointer"
                          onClick={() => setSelectedFlightPlan(plan)}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-slate-700">
                              {plan.flight_purpose || plan.purpose || plan.flightPurpose || '飛行計画'}
                            </span>
                            <Icon name="chevronRight" className="w-3 h-3 text-slate-400" />
                          </div>
                          {(plan.flight_area || plan.flightArea) && (
                            <div className="text-xs text-slate-500 mt-1">
                              <Icon name="mapPin" className="w-3 h-3 inline mr-1" />
                              {plan.flight_area || plan.flightArea}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : flightPlans.length > 0 ? (
            // 単日検索結果表示
            <div className="space-y-2">
              {flightPlans.map((plan, idx) => (
                <div
                  key={idx}
                  className="p-3 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 cursor-pointer transition-colors"
                  onClick={() => setSelectedFlightPlan(plan)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-slate-900">
                        {plan.flight_purpose || plan.purpose || '飛行計画'}
                      </div>
                      <div className="text-sm text-slate-600 mt-1">
                        {formatDate(plan.flight_start_datetime || plan.start_date)} ～ {formatDate(plan.flight_end_datetime || plan.end_date)}
                      </div>
                      {plan.flight_area && (
                        <div className="text-sm text-slate-500 mt-1">
                          <Icon name="mapPin" className="w-3 h-3 inline mr-1" />
                          {plan.flight_area}
                        </div>
                      )}
                    </div>
                    <Badge variant="warning" size="sm">
                      {plan.status || '通報済み'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Icon name="calendar" className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p>{searchDate} の飛行計画はありません</p>
              <p className="text-xs mt-2">「過去2週間〜未来2ヶ月をスキャン」ボタンで広範囲を検索できます</p>
            </div>
          )}
        </Card>
      )}

      {/* 飛行計画詳細モーダル */}
      <Modal
        isOpen={!!selectedFlightPlan}
        onClose={() => setSelectedFlightPlan(null)}
        title="飛行計画詳細"
        size="lg"
      >
        {selectedFlightPlan && (
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4 max-h-96 overflow-auto">
              <pre className="text-xs text-slate-600 whitespace-pre-wrap">
                {JSON.stringify(selectedFlightPlan, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </Modal>

      {/* 機体詳細モーダル */}
      <Modal
        isOpen={!!selectedAircraft}
        onClose={() => setSelectedAircraft(null)}
        title={`機体詳細: ${getAircraftInfo(selectedAircraft)?.registration_code || ''}`}
        size="lg"
      >
        {selectedAircraft && (() => {
          const info = getAircraftInfo(selectedAircraft);
          return (
            <div className="space-y-6">
              {/* 基本情報 */}
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Icon name="info" className="w-4 h-4" />
                  基本情報
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <DetailRow label="登録記号" value={info.registration_code} />
                  <DetailRow label="製造番号" value={info.manufacturing_number} />
                  <DetailRow label="機体種類" value={AIRCRAFT_TYPE_LABELS[info.aircraft_type] || info.aircraft_type} />
                  <DetailRow label="メーカー" value={info.manufacturer_jpn || info.manufacturer_eng} />
                  <DetailRow label="モデル" value={info.model_jpn || info.model_eng} />
                  <DetailRow label="登録日" value={formatDate(info.effectiveness_period_self)} />
                  <DetailRow label="有効期限" value={formatDate(info.effectiveness_period_to)} />
                  <DetailRow label="最終更新日" value={formatDate(info.last_update_date)} />
                  <DetailRow
                    label="ステータス"
                    value={
                      <Badge
                        variant={info.aircraft_status === '1' ? 'success' : 'default'}
                        size="sm"
                      >
                        {info.aircraft_status === '1' ? '有効' :
                         info.aircraft_status === '2' ? '期限切れ' : '抹消済'}
                      </Badge>
                    }
                  />
                </div>
              </div>

              {/* リモートID情報 */}
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Icon name="wifi" className="w-4 h-4" />
                  リモートID情報
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <DetailRow label="RID製造番号" value={info.rid_manufacturing_number} />
                  <DetailRow label="RIDメーカー" value={info.rid_manufacturer_jpn || info.rid_manufacturer_eng} />
                  <DetailRow label="RID型式" value={info.rid_model_jpn || info.rid_model_eng} />
                  <DetailRow label="RID種類" value={info.rid_type === '1' ? '内蔵型' : info.rid_type === '2' ? '外付け型' : info.rid_type} />
                </div>
              </div>

              {/* スペック */}
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Icon name="tool" className="w-4 h-4" />
                  スペック
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <DetailRow label="機体重量" value={info.aircraft_weight ? `${info.aircraft_weight} kg` : '—'} />
                  <DetailRow label="最大離陸重量" value={info.maximum_takeoff_weight ? `${info.maximum_takeoff_weight} kg` : '—'} />
                  <DetailRow label="全長" value={info.aircraft_length ? `${info.aircraft_length} m` : '—'} />
                  <DetailRow label="全幅" value={info.aircraft_width ? `${info.aircraft_width} m` : '—'} />
                  <DetailRow label="全高" value={info.aircraft_height ? `${info.aircraft_height} m` : '—'} />
                </div>
              </div>

              {/* その他の情報（生データ） */}
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Icon name="list" className="w-4 h-4" />
                  生データ（JSON）
                </h4>
                <div className="bg-slate-50 rounded-lg p-4 max-h-64 overflow-auto">
                  <pre className="text-xs text-slate-600 whitespace-pre-wrap">
                    {JSON.stringify(selectedAircraft, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}

// 詳細行コンポーネント
function DetailRow({ label, value }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-slate-400 w-24 flex-shrink-0">{label}:</span>
      <span className="text-slate-900">{value || '—'}</span>
    </div>
  );
}
